import { IconButton, TableCell, TableRow } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import { makeStyles } from '@material-ui/styles'
import React, { useState } from 'react'
import Alert from 'core/components/Alert'
import DropdownField from 'core/components/validatedForm/DropdownField'
import TextField from 'core/components/validatedForm/TextField'
import Button from 'core/elements/button/Button'
import Dropdown from 'core/elements/dropdown'
import MultiDropdown from 'core/elements/dropdown/MultiDropdown'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip/Tooltip'
import Theme from 'core/themes/model'
import { requiredValidator, validIpv4Validator } from 'core/utils/fieldValidators'
import SubnetPicklist from 'openstack/components/networks/network-details/SubnetPicklist'
import { noop, stopPropagation } from 'utils/fp'
import { getPrivateIpValidator } from '../CreateVirtualMachineModal'
import {
  getUsedSubnetIds,
  hasExplicitSubnet,
  isExplicitPort,
  isPrivateIpPort,
  NetworkAttachmentRow,
  NetworkAttachments,
  PORT_AUTOMATIC,
  PORT_PRIVATE_IP,
  SUBNET_AUTOMATIC,
} from '../CreateVirtualMachineModal.helpers'
import {
  createNetworkAttachmentHandlers,
  NetworkAttachmentHandlers,
} from './networkAttachmentHandlers'
import { getFirstAttachmentRow, getSiblingPortIds, MAX_VM_NICS } from './networkAttachmentRows'

const useStyles = makeStyles<Theme>((theme) => ({
  portToolTip: {
    height: '16px',
  },
  portSelection: {
    minWidth: 200,
  },
  fixedIPInput: {},
  subRow: {
    backgroundColor: theme.components.table.hoverBackground,
    borderBottom: `1px solid ${theme.components.table.border}`,
  },
  subRowCell: {
    padding: theme.spacing(2, 1),
    ...theme.typography.body2,
    color: theme?.components?.typography?.default,
  },
  subRowName: {
    color: theme?.components?.typography?.passive,
  },
  subRowEndCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    justifyContent: 'space-between',
  },
  subRowPrivateIp: {
    flex: 1,
    minWidth: 0,
  },
  removeButton: {
    padding: '4px',
    color: theme.components.typography.passive,
    '&:hover': {
      color: theme.components.alert.error.background,
    },
  },
  addSubnetRow: {
    backgroundColor: theme.components.table.hoverBackground,
    borderBottom: `1px solid ${theme.components.table.border}`,
  },
  addSubnetCell: {
    padding: theme.spacing(1, 1, 2, 1),
  },
  addSubnetContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    alignItems: 'flex-start',
  },
}))

/**
 * Table columns for a "simple" network (a Layer 2 network that has no subnets).
 * There's nothing to configure here, so we only show Name, Description, and a
 * Ports column that always reads "Automatic". Used when vmNetworkMode === NetworkMode.SIMPLE.
 */
// FR-022: Options for a Layer 2 network's Ports dropdown. "Automatic" (we create a port) plus
// each unattached port on the network. L2 ports usually have no IP, so the label leads with the
// MAC; the port's IPs are appended only when it happens to have some.
export const getSimplePortOptions = (network: any) => {
  const ports = network?.availablePorts ?? []
  return [
    { label: 'Automatic', value: PORT_AUTOMATIC, noCheckbox: true },
    ...ports.map((port: any) => {
      const ips = port.fixed_ips
        ?.map((ip: any) => ip.ip_address)
        .filter(Boolean)
        .join(', ')
      const name = port.name || port.id
      const label = ips ? `${port.mac_address} — ${ips} (${name})` : `${port.mac_address} (${name})`
      return { label, value: port.id }
    }),
  ]
}

// FR-022: In the Create VM flow we pass { params, getParamsUpdater } so the Ports column is an
// interactive dropdown for picking an existing L2 port. The chosen port is stored on the network's
// single networkAttachments row — the SAME place managed networks keep their port — so the rest of
// the flow (body building, count locking) treats L2 exactly like managed with no special-casing.
// The Clone VM flow calls this with no args, so the Ports column stays the old read-only
// "Automatic" text (L2 port selection is Create-only for now).
export const getSimpleNetworkColumns = (options?: {
  params: { networkAttachments?: NetworkAttachments }
  getParamsUpdater: (key: string) => (value: any) => void
}) => [
  {
    id: 'name',
    label: 'Name',
    render: (name: string) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Text variant="body2">{name}</Text>
      </div>
    ),
  },
  {
    id: 'description',
    label: 'Description',
    render: (description: string) => <Text variant="body2">{description || '-'}</Text>,
  },
  {
    id: 'id',
    label: 'UUID',
    display: false,
  },
  {
    id: 'ports',
    label: options ? (
      <PortsHeaderWithTooltip message="Leave as Automatic to have a port created for you, or select an existing port on this Layer 2 network." />
    ) : (
      <PortsHeaderWithTooltip message="Ports are managed automatically on Layer 2 Networks. To configure ports, use a managed network." />
    ),
    Component: ({ row, isSelected }: { row: any; isSelected: boolean }) => {
      const classes = useStyles()
      // Clone VM flow (no options): keep the old read-only "Automatic" text.
      if (!options) {
        return (
          <div>
            <Text variant="body2">Automatic</Text>
          </div>
        )
      }
      const { params, getParamsUpdater } = options
      const attachments = params.networkAttachments ?? {}
      const handlers = createNetworkAttachmentHandlers(attachments, getParamsUpdater)
      // The port lives on the network's single attachment row (same as managed networks).
      const currentPort = getFirstAttachmentRow(attachments, row.id).port || PORT_AUTOMATIC
      return (
        <div className={classes.portSelection} onClick={isSelected ? stopPropagation : noop}>
          <DropdownField
            id={`${row.id}-l2-ports`}
            DropdownComponent={Dropdown}
            name="ports"
            value={currentPort}
            items={getSimplePortOptions(row)}
            onChange={(value: string) => handlers.setParentPort(row.id, value)}
          />
        </div>
      )
    },
  },
]

/**
 * The "Ports" column header for simple networks: the word "Ports" plus a small
 * help icon that shows the given explanation on hover.
 */
const PortsHeaderWithTooltip = ({ message }: { message: string }) => {
  const classes = useStyles()
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', height: '16px' }}>
      <span>Ports</span>
      <Tooltip className={classes.portToolTip} message={message}>
        <HelpOutlineIcon style={{ fontSize: '16px', color: '#9E9E9E', cursor: 'help' }} />
      </Tooltip>
    </div>
  )
}

interface ManagedNetworkColumnsParams {
  params: {
    selectedPorts: { [networkId: string]: string }
    fixedIPs: { [networkId: string]: string }
    selectedSubnets: { [networkId: string]: any }
    // PCD-5420 FR-014: the new multi-NIC data. It's set in the Create VM flow, but
    // left undefined in the Clone VM modal, which still uses the old single-NIC-per-network
    // cells driven by selectedSubnets/selectedPorts/fixedIPs above (FR-020).
    networkAttachments?: NetworkAttachments
  }
  getParamsUpdater: (key: string) => (value: any) => void
}

// FR-015: Builds the list of choices shown in one row's Ports dropdown.
// A Neutron port (a pre-made virtual network interface) belongs to the whole network, not to a
// single subnet — its subnet only records where its IP was handed out. So when the row is on the
// "Automatic" subnet we offer every one of the network's unused ports; when the row has picked a
// specific subnet we only offer ports that already hold an IP on that subnet (the filtering just
// below). Picking a port never changes the row's subnet — Nova attaches the port by its ID and the
// port brings its own IP. The row's subnet choice only drives the auto-allocation options
// ("Automatic" and "Private IP").
// Ports already chosen by other rows for this network are left out so the same port isn't reused.
export const getRowPortOptions = (
  row: any,
  attachRow: NetworkAttachmentRow,
  excludedPortIds: string[] = [],
) => {
  // If the row has a specific subnet chosen, narrow the ports to just those that already have
  // an IP on that subnet (a dual-stack port counts if it has an IP on either of its subnets).
  // If the subnet is "Automatic" (empty string), every unused port is fair game.
  const subnetId = hasExplicitSubnet(attachRow) ? attachRow.subnet.id : ''
  const availablePorts: any[] = (row?.availablePorts ?? [])
    .filter((port: any) => !excludedPortIds.includes(port.id))
    .filter((port: any) =>
      subnetId ? port.fixed_ips?.some((ip: any) => ip.subnet_id === subnetId) : true,
    )

  if (availablePorts.length) {
    return [
      ...(row?.availableIps > 0
        ? [
            { label: 'Automatic', value: PORT_AUTOMATIC, noCheckbox: true },
            { label: 'Private IP', value: PORT_PRIVATE_IP, noCheckbox: true },
          ]
        : [{ label: 'Select a port (No available IPs)', value: PORT_AUTOMATIC, noCheckbox: true }]),
      ...availablePorts.map((port: any) => {
        const ipAddressString = port.fixed_ips?.map((ip: any) => ip.ip_address)?.join(', ')
        return {
          label: `${ipAddressString} (${port.name || port.id})`,
          value: port.id,
        }
      }),
    ]
  }
  return [
    { label: 'Automatic', value: PORT_AUTOMATIC, noCheckbox: true },
    { label: 'Private IP', value: PORT_PRIVATE_IP, noCheckbox: true },
  ]
}

/**
 * Table columns for a "managed" network (one that has subnets): Name, Subnet, Ports, Private IP.
 * Used when vmNetworkMode === NetworkMode.MANAGED.
 * FR-014: the cells on the network's own row edit its FIRST NIC. Any extra NICs the user adds
 * show up as separate sub-rows below, rendered by getManagedNetworkRowExtension.
 * FR-020: in the Clone VM modal, params.networkAttachments is undefined, so the cells fall back
 * to the old single-NIC maps (selectedSubnets/selectedPorts/fixedIPs) and behave as before.
 */
export const getManagedNetworkColumns = ({
  params,
  getParamsUpdater,
}: ManagedNetworkColumnsParams) => {
  const attachments = params.networkAttachments
  const useAttachments = attachments !== undefined
  const handlers = createNetworkAttachmentHandlers(attachments ?? {}, getParamsUpdater)

  return [
    {
      id: 'name',
      label: 'Name',
      render: (name: string) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Text variant="body2">{name}</Text>
        </div>
      ),
    },
    {
      id: 'id',
      label: 'UUID',
      display: false,
    },
    {
      id: 'subnetDetails',
      label: 'Subnet',
      Component: ({ row, isSelected }: { row: any; isSelected: boolean }) => {
        const firstRow = useAttachments ? getFirstAttachmentRow(attachments, row.id) : null
        // We only want to offer the "Automatic" subnet option when this network has just one
        // NIC and no port-chip NICs. Offering it in other cases would quietly tack on an extra
        // auto-allocated NIC, which isn't what the user means.
        const networkRows = useAttachments ? (attachments[row.id] ?? []) : []
        // Whatever we decide below, an empty subnet ('') always shows as "Automatic" rather than
        // a blank "Select an option" placeholder. That keeps things sensible both when the
        // parent NIC is owned by a port chip and when it's an Automatic NIC sitting next to
        // "+ Add subnet" sub-rows. The guard further down only stops the user from flipping an
        // already-chosen parent subnet back to Automatic while there are several subnet-based
        // sub-rows, so those rows stay distinct.
        // Count only the NICs that come from a subnet pick (ignore port-chip NICs and chip NICs).
        const subnetDrivenRows = networkRows.filter((r) => !r.viaPortChips && !r.chipOf)
        const hasExtraSubnetRows =
          subnetDrivenRows.length > 1 || subnetDrivenRows.some((r) => isExplicitPort(r.port))
        const parentIsAutomatic = !firstRow || firstRow.subnet === SUBNET_AUTOMATIC
        const allowAutomatic = !useAttachments || parentIsAutomatic || !hasExtraSubnetRows
        return (
          <div onClick={isSelected ? stopPropagation : noop}>
            <SubnetPicklist
              networkId={row.id}
              includeAutomaticOption={allowAutomatic}
              label=""
              name={`subnet-${row.id}`}
              value={useAttachments ? firstRow.subnet : (params.selectedSubnets[row.id] ?? '')}
              includeNetworkName={false}
              onChange={(subnet: any) => {
                if (!useAttachments) {
                  getParamsUpdater('selectedSubnets')({
                    ...params.selectedSubnets,
                    [row.id]: subnet,
                  })
                  return
                }
                handlers.setParentSubnet(row.id, subnet)
              }}
            />
          </div>
        )
      },
    },
    {
      id: 'ports',
      label: 'Ports',
      Component: ({ row, isSelected }: { row: any; isSelected: boolean }) => {
        const classes = useStyles()

        if (!useAttachments) {
          // FR-020: old single-port-per-network path, still used by the Clone VM modal.
          const legacyRow = {
            rowId: '',
            subnet: '',
            port: params.selectedPorts[row.id] || '',
            fixedIP: '',
          } as NetworkAttachmentRow
          return (
            <div className={classes.portSelection} onClick={isSelected ? stopPropagation : noop}>
              <DropdownField
                id={`${row.id}-ports`}
                DropdownComponent={Dropdown}
                name="ports"
                value={legacyRow.port}
                items={getRowPortOptions(row, legacyRow)}
                onChange={(value: string) =>
                  getParamsUpdater('selectedPorts')({
                    ...params.selectedPorts,
                    [row.id]: value,
                  })
                }
                required={row?.availableIps === 0 && isSelected}
              />
            </div>
          )
        }

        const rows = attachments[row.id] ?? []
        const firstShownRow = getFirstAttachmentRow(attachments, row.id)

        // Anchor mode: when the network's own row already has a specific subnet chosen, its
        // Ports cell works just like one of the "+ Add subnet" sub-rows below. Ports are limited
        // to that subnet; the first port you pick stays on the parent NIC (keeping its subnet),
        // and any further ports become extra NICs on the same subnet (tied to the parent via
        // chipOf). This is the same wiring as AttachmentSubRow's Ports cell, just with the
        // parent NIC (firstShownRow) as the anchor.
        if (hasExplicitSubnet(firstShownRow)) {
          const anchorValue = isExplicitPort(firstShownRow.port)
            ? [
                firstShownRow.port,
                ...rows.filter((r) => r.chipOf === firstShownRow.rowId).map((r) => r.port),
              ]
            : [firstShownRow.port]
          return (
            <div className={classes.portSelection} onClick={isSelected ? stopPropagation : noop}>
              <MultiDropdown
                id={`${row.id}-ports`}
                name="ports"
                compact={false}
                multiline
                noCheckboxAsLabel
                value={anchorValue}
                items={getRowPortOptions(
                  row,
                  firstShownRow,
                  getSiblingPortIds(attachments, row.id, firstShownRow.rowId),
                )}
                onChange={(newValues: string[]) =>
                  handlers.setSubRowPorts(row.id, firstShownRow, newValues, anchorValue)
                }
              />
            </div>
          )
        }

        // Automatic subnet: a multi-port picker where each chosen port shows as its own chip.
        // Every chip is a separate NIC (these are the viaPortChips rows). The choices cover ALL
        // of the network's unused ports across every subnet on purpose, so the user can grab
        // ports from different subnets here in one place. NICs added below via "+ Add subnet"
        // belong to the sub-row UI and are deliberately never shown here as chips.
        const chipPorts = rows.filter((r) => r.viaPortChips).map((r) => r.port)
        const value = chipPorts.length
          ? chipPorts
          : isExplicitPort(firstShownRow.port)
            ? []
            : [firstShownRow.port]
        return (
          <div className={classes.portSelection} onClick={isSelected ? stopPropagation : noop}>
            <MultiDropdown
              id={`${row.id}-ports`}
              name="ports"
              compact={false}
              multiline
              noCheckboxAsLabel
              value={value}
              items={getRowPortOptions(
                row,
                firstShownRow,
                // Don't offer ports that the "+ Add subnet" sub-rows (and their chips) already use.
                rows.filter((r) => !r.viaPortChips && isExplicitPort(r.port)).map((r) => r.port),
              )}
              onChange={(newValues: string[]) => handlers.setParentPorts(row.id, newValues, value)}
            />
          </div>
        )
      },
    },
    {
      id: 'fixedIP',
      label: 'Private IP',
      Component: ({ row, isSelected }: { row: any; isSelected: boolean }) => {
        const classes = useStyles()
        const firstRow = useAttachments ? getFirstAttachmentRow(attachments, row.id) : null
        const port = useAttachments ? firstRow.port : params.selectedPorts[row.id] || ''
        const fixedIP = useAttachments ? firstRow.fixedIP : params.fixedIPs[row.id]
        return (
          <PrivateIpCell
            row={row}
            isSelected={isSelected}
            port={port}
            fixedIP={fixedIP}
            inputId={`${row.id}-fixedIP`}
            onChangeFixedIP={(value: string) =>
              useAttachments
                ? handlers.setParentFixedIp(row.id, value)
                : getParamsUpdater('fixedIPs')({ ...params.fixedIPs, [row.id]: value })
            }
            classes={classes}
          />
        )
      },
    },
    {
      id: 'port_security_enabled',
      label: 'Port Security',
      render: (val: boolean) => (val ? 'Enabled' : 'Disabled'),
      display: false,
    },
    {
      id: 'provider:network_type',
      label: 'Network Type',
      display: false,
    },
    {
      id: 'mtu',
      label: 'MTU',
      display: false,
    },
    {
      id: 'provider:physical_network',
      label: 'Physical Network',
      display: false,
    },
    {
      id: 'router:external',
      label: 'External',
      render: (val: boolean) => (val ? 'Yes' : 'No'),
      display: false,
    },
    {
      id: 'provider:segmentation_id',
      label: 'Segmentation ID',
      render: (val: string | number | null) => (val ? val : 'N/A'),
      display: false,
    },
    {
      id: 'admin_state_up',
      label: 'Admin State',
      render: (val: boolean) => (val ? 'Up' : 'Down'),
      display: false,
    },
    {
      id: 'dns_domain',
      label: 'DNS Zone',
      render: (val: string | null) => (val ? val : '-'),
      display: false,
    },
    {
      id: 'status',
      label: 'Status',
      display: false,
    },
    {
      id: 'availableIps',
      label: 'Available IPs',
      display: false,
    },
    {
      id: 'shared',
      label: 'Shared Tenants',
      display: false,
    },
    {
      id: 'created_at',
      label: 'Created',
      display: false,
    },
  ]
}

interface PrivateIpCellProps {
  row: any
  isSelected: boolean
  port: string
  fixedIP: string
  inputId: string
  onChangeFixedIP: (value: string) => void
  classes: Record<string, string>
}

// The "Private IP" cell. If the row is set to "Private IP" mode, it shows a text box to type the
// IP into. If the row uses a specific pre-made port instead, it just lists that port's IPs (read-
// only). Otherwise it shows nothing.
const PrivateIpCell = ({
  row,
  isSelected,
  port,
  fixedIP,
  inputId,
  onChangeFixedIP,
  classes,
}: PrivateIpCellProps) => {
  const [localFixedIP, setLocalFixedIP] = useState(fixedIP)
  const isFixedIP = isPrivateIpPort(port)
  const selectedPort = row?.ports?.find((p: any) => p.id === port)

  return isFixedIP ? (
    <div onClick={isSelected ? stopPropagation : noop}>
      <TextField
        id={inputId}
        placeholder="Enter private IP"
        className={classes.fixedIPInput}
        value={localFixedIP}
        onChange={setLocalFixedIP}
        onBlur={() => onChangeFixedIP(localFixedIP)}
        validations={
          isFixedIP && isSelected
            ? [validIpv4Validator, requiredValidator, getPrivateIpValidator(row)]
            : null
        }
      />
    </div>
  ) : selectedPort ? (
    <div>
      {selectedPort?.fixed_ips?.map((ip: any) => (
        <Text key={ip.ip_address} variant="body2">
          {ip.ip_address}
        </Text>
      ))}
    </div>
  ) : null
}

interface AttachmentSubRowProps {
  network: any
  attachRow: NetworkAttachmentRow
  attachments: NetworkAttachments
  handlers: NetworkAttachmentHandlers
}

// FR-014: renders one extra table row for each additional NIC on a network. It mirrors the
// parent columns — an indented network name, then Subnet / Ports / Private IP cells lined up
// under the same headings — with a remove (×) button at the far end.
const AttachmentSubRow = ({ network, attachRow, attachments, handlers }: AttachmentSubRowProps) => {
  const classes = useStyles()
  const rows = attachments[network.id] ?? []
  const chipRows = rows.filter((r) => r.chipOf === attachRow.rowId)
  const portValue = isExplicitPort(attachRow.port)
    ? [attachRow.port, ...chipRows.map((r) => r.port)]
    : [attachRow.port]

  return (
    <TableRow className={classes.subRow}>
      <TableCell padding="checkbox" />
      <TableCell className={`${classes.subRowCell} ${classes.subRowName}`}>
        <Text variant="body2">{network.name}</Text>
      </TableCell>
      <TableCell className={classes.subRowCell}>
        <SubnetPicklist
          networkId={network.id}
          label=""
          name={`subnet-${network.id}-${attachRow.rowId}`}
          value={attachRow.subnet}
          includeNetworkName={false}
          onChange={(subnet: any) => handlers.setSubRowSubnet(network.id, attachRow.rowId, subnet)}
        />
      </TableCell>
      <TableCell className={classes.subRowCell}>
        {/* FR-015: a multi-select of this subnet's unused ports, leaving out any ports already
            taken by other rows or chips. Each port you pick becomes one NIC. */}
        <MultiDropdown
          id={`${network.id}-${attachRow.rowId}-ports`}
          name="ports"
          compact={false}
          multiline
          noCheckboxAsLabel
          value={portValue}
          items={getRowPortOptions(
            network,
            attachRow,
            getSiblingPortIds(attachments, network.id, attachRow.rowId),
          )}
          onChange={(newValues: string[]) =>
            handlers.setSubRowPorts(network.id, attachRow, newValues, portValue)
          }
        />
      </TableCell>
      <TableCell className={classes.subRowCell}>
        <div className={classes.subRowEndCell}>
          <div className={classes.subRowPrivateIp}>
            <PrivateIpCell
              row={network}
              isSelected
              port={attachRow.port}
              fixedIP={attachRow.fixedIP}
              inputId={`${network.id}-${attachRow.rowId}-fixedIP`}
              onChangeFixedIP={(value: string) =>
                handlers.setRowFixedIp(network.id, attachRow.rowId, value)
              }
              classes={classes}
            />
          </div>
          {/* FR-017: each sub-row can be removed on its own. The first NIC (shown on the
              network's own row) has no × — to drop it, the user unchecks the whole network. */}
          <IconButton
            aria-label={`Remove subnet for ${network.name}`}
            className={classes.removeButton}
            size="small"
            onClick={() => handlers.removeRow(network.id, attachRow.rowId)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </TableCell>
    </TableRow>
  )
}

/**
 * FR-014/FR-018: builds the extra rows shown under a selected managed network in the table.
 * For each NIC after the first it renders a sub-row, and below them a full-width
 * "+ Add subnet for {name}" button row for adding another NIC. Returns undefined when there's
 * no networkAttachments data (the Clone VM modal, FR-020), so that table looks unchanged.
 */
export const getManagedNetworkRowExtension = ({
  params,
  getParamsUpdater,
}: ManagedNetworkColumnsParams) => {
  const attachments = params.networkAttachments
  if (attachments === undefined) {
    return undefined
  }
  const handlers = createNetworkAttachmentHandlers(attachments, getParamsUpdater)

  const RowExtension = ({ row, colSpan }: { row: any; colSpan: number }) => {
    const classes = useStyles()
    const rows = attachments[row.id] ?? []
    // The sub-rows we draw are the NICs added via "+ Add subnet" (each has its own subnet and
    // a port dropdown filtered to that subnet). We skip the first NIC (already on the network's
    // own row) and skip port-chip NICs (those only appear as chips on the parent row).
    const firstShown = getFirstAttachmentRow(attachments, row.id)
    const extraRows = rows.filter(
      (r) => r.rowId !== firstShown.rowId && !r.viaPortChips && !r.chipOf,
    )
    // FR-018 (per PM): putting several NICs on the same subnet is allowed, so we just warn
    // about it rather than block it.
    const explicitSubnetIds = getUsedSubnetIds(attachments, row.id)
    const hasDuplicateSubnets = new Set(explicitSubnetIds).size !== explicitSubnetIds.length
    // Two separate limits on adding NICs: (1) each NIC needs either a free IP or an unused port,
    // so we cap how many this network can hand out; (2) a VM can only have so many NICs total
    // across all its networks (a practical hardware slot limit).
    const maxAttachments = row?.availablePortAndIPs ?? Infinity
    const totalVmNics = Object.values(attachments).reduce(
      (total, networkRows) => total + Math.max(networkRows.length, 1),
      0,
    )
    const networkAtCapacity = Math.max(rows.length, 1) >= maxAttachments
    const vmAtNicLimit = totalVmNics >= MAX_VM_NICS
    const canAddRow = !networkAtCapacity && !vmAtNicLimit
    const addDisabledReason = networkAtCapacity
      ? `${row.name} has no more available IPs or unassigned ports`
      : `A VM supports a maximum of ${MAX_VM_NICS} network interfaces`

    return (
      <>
        {extraRows.map((attachRow) => (
          <AttachmentSubRow
            key={attachRow.rowId}
            network={row}
            attachRow={attachRow}
            attachments={attachments}
            handlers={handlers}
          />
        ))}
        <TableRow className={classes.addSubnetRow}>
          <TableCell padding="checkbox" />
          <TableCell colSpan={colSpan - 1} className={classes.addSubnetCell}>
            <div className={classes.addSubnetContent}>
              <Button
                variant="secondary"
                icon="plus"
                disabled={!canAddRow}
                tooltip={canAddRow ? undefined : addDisabledReason}
                onClick={() => handlers.addRow(row.id, row)}
              >
                Add subnet for {row.name}
              </Button>
              {hasDuplicateSubnets && (
                <Alert
                  variant="warning"
                  message="Multiple interfaces on the same subnet can cause routing issues in the guest unless source routing is configured."
                />
              )}
            </div>
          </TableCell>
        </TableRow>
      </>
    )
  }

  return (row: any, { isSelected, colSpan }: { isSelected: boolean; colSpan: number }) => {
    // Only selected managed networks get sub-rows and the "+ Add subnet" button.
    if (!isSelected) {
      return null
    }
    return <RowExtension key={`${row.id}-attachments`} row={row} colSpan={colSpan} />
  }
}
